import {
  MediaAssetPollingProvider,
  useMediaAssetPollingContext,
} from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import useMediaAssetsLoader from '@/hooks/useMediaAssetsLoader';
import { MediaAssetPurpose, MediaAssetStatus, StandaloneMediaAsset } from '@/types/media';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useMemo } from 'react';
import EmptyGrid from '@/components/EmptyGrid/EmptyGrid';
import { differenceInMinutes } from 'date-fns';
import { MediaGridSize } from '@/components/media-asset/MediaGrid';
type MediaAssetsLoaderProps = {
  queryKey: string[];
  loaderFn: (props: { appKey: string }) => Promise<{ mediaAssets: StandaloneMediaAsset[] }>;
  renderMediaAssets: (mediaAssets: StandaloneMediaAsset[]) => React.ReactNode;
  hideFailed?: boolean;
  notifyAboutFailedItems?: (mediaAssets: StandaloneMediaAsset[]) => void;
  size?: MediaGridSize;
  staticHeight?: number | string;
};

const MediaAssetsLoaderComponent = ({
  queryKey,
  loaderFn,
  renderMediaAssets,
  notifyAboutFailedItems,
  hideFailed,
  size,
  staticHeight,
}: MediaAssetsLoaderProps) => {
  const { setPollingQueue } = useMediaAssetPollingContext();
  const { mediaAssets, isLoading } = useMediaAssetsLoader({
    queryKey,
    loaderFn,
  });

  const { renderedMediaAssets, failedMediaAssets, pendingMediaAssets } = useMemo(() => {
    if (!mediaAssets) return { renderedMediaAssets: [], failedMediaAssets: [], pendingMediaAssets: [] };
    const renderedMediaAssets: StandaloneMediaAsset[] = [];
    const failedMediaAssets: StandaloneMediaAsset[] = [];
    const pendingMediaAssets: StandaloneMediaAsset[] = [];
    mediaAssets?.forEach((mediaAsset) => {
      if (mediaAsset.purpose === MediaAssetPurpose.personalAvatarImage) {
        // skipping to not show personal avatar images as "Image" assets
        return;
      }

      const isFailed = mediaAsset.status === MediaAssetStatus.FAILED;
      if (isFailed) {
        failedMediaAssets.push(mediaAsset);
      } else {
        if (
          mediaAsset.status === MediaAssetStatus.PROCESSING &&
          differenceInMinutes(new Date(), new Date(mediaAsset.updatedAt)) < 60
        ) {
          pendingMediaAssets.push(mediaAsset);
        }
        renderedMediaAssets.push(mediaAsset);
      }
    });
    return { renderedMediaAssets, failedMediaAssets, pendingMediaAssets };
  }, [mediaAssets]);

  useEffect(() => {
    if (failedMediaAssets?.length > 0 && notifyAboutFailedItems) {
      notifyAboutFailedItems(failedMediaAssets);
    }
  }, [failedMediaAssets, notifyAboutFailedItems]);

  useEffect(() => {
    if (pendingMediaAssets?.length) {
      setPollingQueue(pendingMediaAssets);
    }
    return () => setPollingQueue([]);
  }, [pendingMediaAssets, setPollingQueue]);

  if (isLoading || !renderedMediaAssets) {
    return <EmptyGrid size={size} staticHeight={staticHeight} />;
  }

  return renderMediaAssets(renderedMediaAssets);
};

const MediaAssetsLoader = (props: MediaAssetsLoaderProps) => {
  const queryClient = useQueryClient();
  const updateMediaAssetsInCache = useCallback(
    (updatedMediaAssets: StandaloneMediaAsset[]) => {
      queryClient.setQueryData<{ mediaAssets: StandaloneMediaAsset[] }>(props.queryKey, (oldData) => {
        if (!oldData) return { mediaAssets: [] };
        return {
          mediaAssets: oldData.mediaAssets.map((oldAsset) => {
            const updatedAsset = updatedMediaAssets.find((newAsset) => newAsset.id === oldAsset.id);
            return updatedAsset || oldAsset;
          }),
        };
      });
    },
    [queryClient, props.queryKey]
  );
  return (
    <MediaAssetPollingProvider onCompletion={updateMediaAssetsInCache}>
      <MediaAssetsLoaderComponent {...props} />
    </MediaAssetPollingProvider>
  );
};

export default memo(MediaAssetsLoader);
